import React from "react";

const firstName = (fullName) =>
  (fullName || "").trim().split(/\s+/)[0]?.toUpperCase() || "CANDIDATE";

const SectionH = ({ children, accent }) => (
  <h2 className="cv-section-h" style={accent ? { color: accent } : undefined}>
    {children}
  </h2>
);

const Role = ({ r, accent }) => (
  <div className="cv-role avoid-break">
    <div
      className="cv-role-title"
      style={accent ? { color: accent } : undefined}
    >
      {[r.job_title, r.company].filter(Boolean).join(" - ").toUpperCase()}
    </div>
    {r.location && <div className="cv-role-line">{r.location}</div>}
    {(r.start_date || r.end_date) && (
      <div className="cv-role-line">
        {r.start_date}
        {r.start_date && r.end_date ? " - " : ""}
        {r.end_date}
      </div>
    )}
    {r.bullets && r.bullets.length > 0 && (
      <ul className="cv-bullets">
        {r.bullets.filter(Boolean).map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    )}
  </div>
);

const PageFill = () => (
  <div className="cv-page-fill" aria-hidden="true">
    <span>TRAM</span>
  </div>
);

export default function TramTemplate({ cv }) {
  const accent = cv.preset?.accent_color || "";
  const fname = firstName(cv.full_name);
  const hasDetails =
    cv.nationality || cv.location || cv.age || cv.languages || cv.email || cv.phone;

  return (
    <div className="cv-doc" data-testid="tram-cv-preview">
      {/* ================= PAGE 1: HERO · DETAILS · SUMMARY ================= */}
      <div className="cv-page">
        <div className="cv-page-content">
          <div className="cv-hero avoid-break">
            <div className="cv-hero-band">
              <span className="cv-hero-watermark">TRAM</span>
              <div className="cv-hero-inner">
                <div className="cv-hero-name">{fname}</div>
                <div className="cv-hero-underline" />
                <div
                  className="cv-hero-tag"
                  style={accent ? { color: accent } : undefined}
                >
                  {(cv.preset?.tagline || cv.title || "Recruitment Candidate Profile").toUpperCase()}
                </div>
              </div>
            </div>
            <div
              className="cv-hero-photo"
              style={accent ? { borderColor: accent } : undefined}
            >
              {cv.photo_data_url ? (
                <img
                  src={cv.photo_data_url}
                  alt="candidate"
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="cv-hero-photo-placeholder">
                  <span>Photo</span>
                </div>
              )}
            </div>
          </div>

          {hasDetails && (
            <div className="cv-details-strip avoid-break">
              {cv.nationality && (
                <div>
                  <div className="cv-detail-label">Nationality</div>
                  <div className="cv-detail-value">{cv.nationality}</div>
                </div>
              )}
              {cv.location && (
                <div>
                  <div className="cv-detail-label">Current Location</div>
                  <div className="cv-detail-value">{cv.location}</div>
                </div>
              )}
              {cv.age && (
                <div>
                  <div className="cv-detail-label">Age</div>
                  <div className="cv-detail-value">{cv.age}</div>
                </div>
              )}
              {cv.languages && (
                <div>
                  <div className="cv-detail-label">Languages</div>
                  <div className="cv-detail-value">{cv.languages}</div>
                </div>
              )}
              {cv.email && (
                <div>
                  <div className="cv-detail-label">Email</div>
                  <div className="cv-detail-value">{cv.email}</div>
                </div>
              )}
              {cv.phone && (
                <div>
                  <div className="cv-detail-label">Phone</div>
                  <div className="cv-detail-value">{cv.phone}</div>
                </div>
              )}
            </div>
          )}

          <div className="cv-main">
            <div className="cv-block">
              <SectionH accent={accent}>Personal Summary</SectionH>
              <p className="cv-p">
                {cv.personal_summary ||
                  "Personal summary will appear here once you paste or upload a CV."}
              </p>
            </div>
          </div>
        </div>
        <PageFill />
      </div>

      {/* ================= PAGE 2: EXPERIENCE ================= */}
      <div className="cv-page">
        <div className="cv-page-content">
          <div className="cv-main">
            {cv.work_experience?.length > 0 && (
              <div className="cv-block">
                <SectionH accent={accent}>Work Experience</SectionH>
                {cv.work_experience.map((r, i) => (
                  <Role key={i} r={r} accent={accent} />
                ))}
              </div>
            )}
            {cv.previous_employment?.length > 0 && (
              <div className="cv-block">
                <SectionH accent={accent}>Previous Employment</SectionH>
                <ul className="cv-bullets">
                  {cv.previous_employment.map((p, i) => (
                    <li key={i}>
                      <span className="cv-bold">{p.role}</span>
                      {p.company && ` · ${p.company}`}
                      {p.years && ` · ${p.years}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <PageFill />
      </div>

      {/* ================= PAGE 3: EDUCATION · SKILLS · FOOTER ================= */}
      <div className="cv-page cv-page-last">
        <div className="cv-page-content">
          <div className="cv-main">
            <div className="cv-endgrid">
              <div>
                {cv.education?.length > 0 && (
                  <div className="cv-block avoid-break">
                    <SectionH accent={accent}>Education and Courses</SectionH>
                    {cv.education.map((e, i) => (
                      <div key={i} className="cv-edu">
                        <div className="cv-bold">{e.qualification}</div>
                        {e.institution && (
                          <div className="cv-role-line">{e.institution}</div>
                        )}
                        {e.year && <div className="cv-role-line">{e.year}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {cv.certifications?.length > 0 && (
                  <div className="cv-block avoid-break">
                    <SectionH accent={accent}>Professional Certifications</SectionH>
                    <ul className="cv-bullets">
                      {cv.certifications.map((c, i) => (
                        <li key={i}>
                          <span className="cv-bold">{c.name}</span>
                          {c.issuer_year && ` · ${c.issuer_year}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                {cv.competencies?.length > 0 && (
                  <div className="cv-block avoid-break">
                    <SectionH accent={accent}>Skills</SectionH>
                    <ul className="cv-skills">
                      {cv.competencies.map((c, i) => (
                        <li key={i}>· {c}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {cv.career_highlights?.length > 0 && (
                  <div className="cv-block avoid-break">
                    <SectionH accent={accent}>Industry Awards & Recognition</SectionH>
                    <ul className="cv-bullets">
                      {cv.career_highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <PageFill />
        <div className="cv-footer">
          <span className="cv-footer-mark">TRAM</span>
          <span className="cv-footer-mark">TRAM</span>
        </div>
      </div>
    </div>
  );
}
